package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.ListingDetailContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.admin.util.formatPriceCents
import com.merkost.handpass.admin.util.LocalViewportMode
import com.merkost.handpass.admin.util.ViewportMode
import com.merkost.handpass.api.dto.admin.AdminListingDetail
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

val SellerLinkStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Accent.value())
            .cursor(Cursor.Pointer)
            .borderRadius(RadiusControl.value())
            .textDecorationLine(TextDecorationLine.None)
            .outline(0.px, LineStyle.None, Color("transparent"))
    }
    hover {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
    focusVisible {
        Modifier
            .outline(0.px, LineStyle.None, Color("transparent"))
            .boxShadow(spreadRadius = 3.px, color = AccentRing.value())
    }
}

@Page("/listings/{id}")
@Composable
fun ListingDetailPage() {
    val ctx = rememberPageContext()
    val listingId = ctx.route.params["id"] ?: ""
    AdminPageScaffold(title = "Listing Detail", currentPath = "/listings", showTitle = false) {
        val container = koinContainer<ListingDetailContainer>()
        var pendingConfirm by remember { mutableStateOf<Pair<ListingDetailActionKind, ConfirmConfig>?>(null) }
        var pendingDeleteNoteId by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(listingId) { container.store.intent(ListingDetailIntent.Init(listingId)) }

        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is ListingDetailAction.Navigate -> navigate(action.path)
                    is ListingDetailAction.ShowSuccess -> toast(action.message, ToastKind.Success)
                    is ListingDetailAction.ShowUndoableSuccess -> showUndoToast(
                        message = action.message,
                        onUndo = { intent(ListingDetailIntent.UndoRestore(action.listingId)) },
                    )
                    is ListingDetailAction.ShowError -> toast(action.message, ToastKind.Error)
                    is ListingDetailAction.OpenReasonModal -> pendingConfirm = action.kind to confirmConfigFor(action.kind, action.targetTitle)
                }
            }
            when (val s = state) {
                ListingDetailState.Loading -> AdminCard { AdminSkeletonTable(rows = 4) }
                is ListingDetailState.Error -> AdminEmptyState(
                    title = "Failed to load",
                    description = s.message,
                    actionLabel = "Retry",
                    onAction = { intent(ListingDetailIntent.Refresh) },
                )
                is ListingDetailState.Content -> ListingDetailContent(
                    s = s,
                    onIntent = ::intent,
                    onBack = { navigate("/listings") },
                    onRequestDeleteNote = { pendingDeleteNoteId = it },
                )
            }

            pendingConfirm?.let { (kind, config) ->
                AdminConfirmDialog(
                    config = config,
                    onConfirm = { reason ->
                        intent(ListingDetailIntent.ConfirmAction(kind, reason))
                        pendingConfirm = null
                    },
                    onDismiss = { pendingConfirm = null },
                )
            }

            pendingDeleteNoteId?.let { noteId ->
                AdminConfirmDialog(
                    config = ConfirmConfig(
                        title = "Delete note",
                        message = "Delete this admin note? This can't be undone.",
                        confirmLabel = "Delete",
                        isDestructive = true,
                    ),
                    onConfirm = {
                        intent(ListingDetailIntent.DeleteNote(noteId))
                        pendingDeleteNoteId = null
                    },
                    onDismiss = { pendingDeleteNoteId = null },
                )
            }
        }
    }
}

private fun confirmConfigFor(kind: ListingDetailActionKind, title: String): ConfirmConfig = when (kind) {
    ListingDetailActionKind.Suspend -> ConfirmConfig(
        title = "Suspend listing",
        message = "Suspend \"$title\"?",
        confirmLabel = "Suspend",
        isDestructive = true,
        reasonRequired = true,
    )
    ListingDetailActionKind.Restore -> ConfirmConfig(
        title = "Restore listing",
        message = "Restore \"$title\"?",
        confirmLabel = "Restore",
    )
}

@Composable
private fun AdminPageScope.ListingDetailContent(
    s: ListingDetailState.Content,
    onIntent: (ListingDetailIntent) -> Unit,
    onBack: () -> Unit,
    onRequestDeleteNote: (String) -> Unit,
) {
    AdminPageHeader(
        title = s.detail.title,
        subtitle = "${formatPriceCents(s.detail.priceCents)} ${s.detail.currency}",
        actions = {
            AdminButton(label = "← Listings", onClick = onBack, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        },
    )

    AdminTabs(
        tabs = listOf(
            AdminTab(ListingDetailTab.Overview, "Overview"),
            AdminTab(ListingDetailTab.Notes, "Notes"),
            AdminTab(ListingDetailTab.Conversations, "Conversations"),
            AdminTab(ListingDetailTab.ViewedBy, "Viewed by"),
        ),
        selected = s.activeTab,
        onSelect = { onIntent(ListingDetailIntent.SetTab(it)) },
    )

    when (s.activeTab) {
        ListingDetailTab.Overview -> ListingDetailOverviewTab(detail = s.detail, onIntent = onIntent, onBack = onBack)
        ListingDetailTab.Notes -> AdminNotesSection(
            draft = s.newNoteDraft,
            notes = s.notes,
            onDraftChange = { onIntent(ListingDetailIntent.SetNoteDraft(it)) },
            onSubmit = { onIntent(ListingDetailIntent.SubmitNote) },
            onRequestDelete = onRequestDeleteNote,
            onRetry = { onIntent(ListingDetailIntent.Refresh) },
        )
        ListingDetailTab.Conversations -> AdminConversationsView(
            conversations = s.conversations,
            openConversation = s.openConversation,
            messages = s.messages,
            onOpen = { onIntent(ListingDetailIntent.OpenConversation(it)) },
            onClose = { onIntent(ListingDetailIntent.CloseConversation) },
            onRetryConversations = { onIntent(ListingDetailIntent.LoadConversations) },
            onRetryMessages = { s.openConversation?.let { onIntent(ListingDetailIntent.OpenConversation(it)) } },
        )
        ListingDetailTab.ViewedBy -> AdminViewedBySection(
            audit = s.audit,
            onRetry = { onIntent(ListingDetailIntent.Refresh) },
        )
    }
}

@Composable
private fun AdminPageScope.ListingDetailOverviewTab(
    detail: AdminListingDetail,
    onIntent: (ListingDetailIntent) -> Unit,
    onBack: () -> Unit,
) {
    val viewportMode = LocalViewportMode.current
    val columns = if (viewportMode == ViewportMode.Wide) "1fr 360px" else "1fr"
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", columns)
            gap(HandpassSpacing.XL)
            alignItems(AlignItems.FlexStart)
            property("margin-top", "${HandpassSpacing.Default}")
        }
    }) {
        ListingImageGallery(title = detail.title, imageUrls = detail.imageUrls)

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(HandpassSpacing.Default)
            }
        }) {
            AdminCard(paddingScale = AdminCardPadding.Spacious) {
                H2({
                    style {
                        fontSize(HandpassTypography.Title)
                        fontWeight(700)
                        property("margin", "0 0 ${HandpassSpacing.SM} 0")
                        color(OnSurface.value())
                    }
                }) { Text(detail.title) }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(HandpassSpacing.SM)
                        property("margin-bottom", "${HandpassSpacing.Default}")
                    }
                }) {
                    AccountStatusBadge(detail.status)
                    Span({
                        style {
                            fontSize(HandpassTypography.LG)
                            fontWeight(700)
                            color(Accent.value())
                        }
                    }) { Text("${formatPriceCents(detail.priceCents)} ${detail.currency}") }
                }

                detail.categoryName?.let { cat ->
                    Div({
                        style {
                            fontSize(HandpassTypography.Body)
                            color(OnSurfaceMuted.value())
                            property("margin-bottom", "${HandpassSpacing.XS}")
                        }
                    }) { Text("Category: $cat") }
                }

                detail.condition?.let { cond ->
                    Div({
                        style {
                            fontSize(HandpassTypography.Body)
                            color(OnSurfaceMuted.value())
                            property("margin-bottom", "${HandpassSpacing.XS}")
                        }
                    }) { Text("Condition: $cond") }
                }

                Div({
                    style {
                        fontSize(HandpassTypography.Body)
                        color(OnSurfaceMuted.value())
                        property("margin-bottom", "${HandpassSpacing.XS}")
                    }
                }) { Text("Created: ${detail.createdAt.take(10)}") }

                val place = listOfNotNull(detail.suburb, detail.city, detail.state).filter { it.isNotBlank() }.joinToString(", ")
                if (place.isNotBlank()) {
                    Div({
                        style {
                            fontSize(HandpassTypography.Body)
                            color(OnSurfaceMuted.value())
                            property("margin-bottom", "${HandpassSpacing.XS}")
                        }
                    }) { Text("Location: $place") }
                }

                detail.sellerName?.let { name ->
                    A(
                        href = "#",
                        attrs = SellerLinkStyle.toAttrs {
                            onClick { event ->
                                event.preventDefault()
                                detail.sellerId?.let { navigate("/users/$it") }
                            }
                            style {
                                fontSize(HandpassTypography.Body)
                                property("margin-bottom", "${HandpassSpacing.XS}")
                            }
                        },
                    ) { Text("Seller: $name") }
                }

                if (detail.reports.isNotEmpty()) {
                    Div({
                        style {
                            property("background-color", "${AccentSoft.value()}")
                            borderRadius(HandpassRadius.XS)
                            padding(HandpassSpacing.SM)
                            property("margin-top", "${HandpassSpacing.Default}")
                        }
                    }) {
                        Span({
                            style {
                                fontSize(HandpassTypography.Body)
                                color(Warning.value())
                            }
                        }) { Text("${detail.reports.size} report(s) against this listing") }
                    }
                }
            }

            AdminCard(paddingScale = AdminCardPadding.Default) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(HandpassSpacing.SM)
                    }
                }) {
                    AdminButton(
                        label = "Suspend Listing",
                        onClick = { onIntent(ListingDetailIntent.TapSuspend) },
                        variant = AdminButtonVariant.Destructive,
                        size = AdminButtonSize.LG,
                    )
                    AdminButton(
                        label = "Restore Listing",
                        onClick = { onIntent(ListingDetailIntent.TapRestore) },
                        variant = AdminButtonVariant.Secondary,
                        size = AdminButtonSize.LG,
                    )
                    AdminButton(
                        label = "← Back",
                        onClick = onBack,
                        variant = AdminButtonVariant.Ghost,
                        size = AdminButtonSize.MD,
                    )
                }
            }
        }
    }
}

@Composable
private fun ListingImageGallery(title: String, imageUrls: List<String>) {
    var selectedImage by remember { mutableStateOf(0) }

    Div {
        if (imageUrls.isNotEmpty()) {
            Img(src = imageUrls[selectedImage], alt = "$title image ${selectedImage + 1}") {
                style {
                    width(100.percent)
                    property("max-height", "480px")
                    property("object-fit", "cover")
                    borderRadius(HandpassRadius.MD)
                    property("margin-bottom", "${HandpassSpacing.Default}")
                }
            }
            if (imageUrls.size > 1) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(HandpassSpacing.SM)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    imageUrls.forEachIndexed { i, url ->
                        val isSelected = i == selectedImage
                        Button(attrs = {
                            onClick { selectedImage = i }
                            attr("aria-label", "View image ${i + 1}")
                            attr("aria-pressed", isSelected.toString())
                            style {
                                width(72.px)
                                height(72.px)
                                padding(0.px)
                                property("background", "transparent")
                                borderRadius(HandpassRadius.XS)
                                property("overflow", "hidden")
                                cursor("pointer")
                                property("border", if (isSelected) "2px solid ${Accent.value()}" else "2px solid ${Border.value()}")
                                property("box-shadow", if (isSelected) "0 0 0 3px ${AccentRing.value()}" else "none")
                                property("outline", "none")
                            }
                        }) {
                            Img(src = url, alt = "") {
                                style {
                                    width(100.percent)
                                    height(100.percent)
                                    property("object-fit", "cover")
                                    property("display", "block")
                                }
                            }
                        }
                    }
                }
            }
        } else {
            Div({
                style {
                    width(100.percent)
                    height(320.px)
                    property("background-color", SurfaceMuted.value().toString())
                    borderRadius(HandpassRadius.MD)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                Span({ style { color(OnSurfaceFaint.value()) } }) { Text("No images") }
            }
        }
    }
}
